import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Brand } from "@/components/Brand";
import { PanicBanner } from "@/components/PanicBanner";
import { PanicButton } from "@/components/PanicButton";
import { OperationalInbox } from "@/components/OperationalInbox";
import { api } from "@/lib/api";
import { toast } from "@/components/Toast";
import { onPanicChanged } from "@/lib/panicEvents";
import {
  LayoutDashboard, Building2, MapPinned, FileText, Inbox, ClipboardList,
  LogOut, Menu, X, AlertOctagon, Truck, Footprints, UserCheck, MessageSquare,
  BookOpen, Package, Palette, ClipboardCheck, Boxes, ShieldCheck, ListChecks, CheckSquare, CalendarRange, Radar, Clock, Route, MapPin, Smartphone, History, ShoppingCart, Activity, UserCircle,
} from "lucide-react";

const ITEMS = [
  { to: "/supervisor", end: true, label: "Inicio", icon: LayoutDashboard },
  { to: "/supervisor/alertas", label: "Alertas", icon: AlertOctagon },
  { to: "/supervisor/servicio", label: "Servicios Abiertos", icon: Activity },
  { to: "/supervisor/servicios", label: "Mis Servicios", icon: ClipboardList },
  { to: "/supervisor/checkin", label: "Check-in en Objetivo", icon: MapPin },
  { to: "/supervisor/chats", label: "Chats", icon: MessageSquare },
  { to: "/supervisor/rondas", label: "Rondas", icon: MapPinned },
  { to: "/supervisor/rondas-vivo", label: "Rondas en vivo", icon: Radar },
  { to: "/supervisor/novedades", label: "Novedades", icon: FileText },
  { to: "/supervisor/solicitudes", label: "Solicitudes", icon: Inbox },
  { to: "/supervisor/vehiculos", label: "Vehículos", icon: Truck },
  { to: "/supervisor/visitas", label: "Visitas", icon: Footprints },
  { to: "/supervisor/personal", label: "Personal Autorizado", icon: UserCheck },
  { to: "/supervisor/materiales", label: "Elementos de Trabajo (Cliente)", icon: Package },
  { to: "/supervisor/elementos", label: "Elementos de Trabajo", icon: ClipboardCheck },
  { to: "/supervisor/logistica", label: "Logística / Recursos", icon: Boxes },
  { to: "/supervisor/celulares", label: "Celulares Corporativos", icon: Smartphone },
  { to: "/supervisor/guardias", label: "Guardias Pasivas", icon: ShieldCheck },
  { to: "/supervisor/cronograma", label: "Cronograma", icon: CalendarRange },
  { to: "/supervisor/horas-extra", label: "Horas Extra", icon: Clock },
  // Fix A (2026-08-28): renombrado a "Supervisores" (la ruta se mantiene).
  { to: "/supervisor/recorridos", label: "Supervisores", icon: Route },
  { to: "/supervisor/marcaciones", label: "Marcaciones de Supervisores", icon: History },
  { to: "/supervisor/cuestionarios", label: "Cuestionarios", icon: ListChecks },
  { to: "/supervisor/checklist-servicio", label: "Checklist de Servicio", icon: CheckSquare },
  { to: "/supervisor/manual", label: "Manual", icon: BookOpen },
  { to: "/supervisor/marca", label: "Marca", icon: Palette },
  { to: "/supervisor/clientes", label: "Clientes", icon: Building2 },
  { to: "/supervisor/tareas-operativas", label: "Tareas Operativas", icon: ShoppingCart },
];

export default function SupervisorLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [panicCount, setPanicCount] = useState(0);
  const [pendingReq, setPendingReq] = useState(0);
  const [checklistAlerts, setChecklistAlerts] = useState(0);
  const lastUnread = useRef(0);
  const lastReq = useRef(0);
  const lastPanic = useRef(0);
  const lastChecklist = useRef(0);
  const firstTick = useRef(true);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const [u, p, r, w] = await Promise.all([
          api.get("/chats/unread-count"),
          api.get("/panic/active-count"),
          api.get("/internal-requests/pending-count"),
          api.get("/work-items/compliance-count"),
        ]);
        if (cancelled) return;
        const nu = u.data.count || 0;
        const np = p.data.count || 0;
        const nr = r.data.count || 0;
        const nw = w.data.total || 0;
        if (!firstTick.current) {
          if (nu > lastUnread.current) toast.info(`Nuevo mensaje en chats (${nu} sin leer)`);
          if (nr > lastReq.current) toast.info(`Nueva solicitud pendiente (${nr} en total)`);
          if (np > lastPanic.current) toast.error(`Alerta de pánico activa (${np})`);
          if (nw > lastChecklist.current) toast.error(`Incumplimiento en Elementos de Trabajo (${nw})`);
        }
        lastUnread.current = nu;
        lastReq.current = nr;
        lastPanic.current = np;
        lastChecklist.current = nw;
        firstTick.current = false;
        setUnread(nu);
        setPanicCount(np);
        setPendingReq(nr);
        setChecklistAlerts(nw);
      } catch {/* silent */}
    };
    tick();
    const i = setInterval(tick, 8000);
    const offPanicChanged = onPanicChanged(tick); // refleja al instante attend/resolve/bulk-resolve, sin esperar el próximo tick
    return () => { cancelled = true; clearInterval(i); offPanicChanged(); };
  }, []);

  const onLogout = async () => { await logout(); nav("/login"); };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--invict-bone)" }}>
      <div className="admin-mobile-topbar md:hidden">
        <Brand size="sm" iconOnly />
        <button onClick={() => setOpen(!open)} style={{ background: "none", border: "none", color: "#FFFBF4" }} data-testid="sup-menu-toggle">
          {open ? <X size={22}/> : <Menu size={22}/>}
        </button>
      </div>

      <aside
        style={{ width: 240, background: "#0A0A0A", color: "#FFFBF4", position: "fixed", top: 0, bottom: 0, left: 0, display: "flex", flexDirection: "column", padding: "26px 0 18px", transform: open ? "translateX(0)" : undefined, zIndex: 60 }}
        className={`shrink-0 ${open ? "" : "max-md:-translate-x-full"} transition-transform`}
        data-testid="supervisor-sidebar"
      >
        <div style={{ padding: "0 22px 22px", borderBottom: "1px solid rgba(255,251,244,0.08)" }}>
          <Brand size="md" iconOnly />
        </div>
        <nav style={{ marginTop: 16, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
          {ITEMS.map(({ to, label, icon: Ic, end }) => {
            const showBadge = label === "Chats" && unread > 0;
            const showAlert = label === "Alertas" && panicCount > 0;
            const showReqBadge = label === "Solicitudes" && pendingReq > 0;
            const showChecklistBadge = label === "Elementos de Trabajo" && checklistAlerts > 0;
            return (
              <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Ic size={16} /> {label}
                {showBadge && <span data-testid="badge-chats" style={{ marginLeft: "auto", background: "#BF3437", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{unread}</span>}
                {showAlert && <span style={{ marginLeft: "auto", background: "#BF3437", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{panicCount}</span>}
                {showReqBadge && <span data-testid="badge-requests" style={{ marginLeft: "auto", background: "#BF3437", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{pendingReq}</span>}
                {showChecklistBadge && <span data-testid="badge-checklist" style={{ marginLeft: "auto", background: "#BF3437", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{checklistAlerts}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div style={{ padding: "16px 18px", borderTop: "1px solid rgba(255,251,244,0.08)", flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: "rgba(255,251,244,0.5)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Sesión</div>
          <div style={{ fontSize: 13, color: "#FFFBF4", fontWeight: 700, marginTop: 4 }}>{user?.name}</div>
          <div style={{ fontSize: 11, color: "rgba(255,251,244,0.5)" }}>Supervisor</div>
          <Link to="/supervisor/perfil" onClick={() => setOpen(false)} className="sidebar-link" style={{ marginTop: 10, padding: "8px 0", borderLeft: "none" }} data-testid="nav-perfil">
            <UserCircle size={14} /> Perfil
          </Link>
          <button onClick={onLogout} className="sidebar-link" style={{ marginTop: 2, padding: "10px 0", borderLeft: "none", color: "#BF3437" }} data-testid="logout-button">
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="md:ml-[240px] flex-1" style={{ minHeight: "100vh", paddingTop: 16, paddingBottom: "calc(110px + env(safe-area-inset-bottom, 0px))" }}>
        <div className="md:hidden" style={{ height: 50 }} />
        <PanicBanner />
        <OperationalInbox />
        <Outlet />
        <PanicButton />
      </main>
    </div>
  );
}
